/**
 * This page is responsible for rendering the stripe payment form that allows
 * users to check out their orders.
 *
 * The basic flow of the page is as follows:
 * 1. On every page load, we request a transaction from the backend for the
 *    current draft order. If there's not already a "Processing" or "Paid"
 *    transaction, a new one is generated for it with a status of "Unpaid".
 * 2. The user fills out their billing information and payment details, and clicks
 *    the "Place order" button, triggering an order submission.
 * 3. We request a lock on the order by requesting that the backend "prepare"
 *    the current transaction. While this lock is held, new transactions cannot
 *    be created for the order.
 * 4. We confirm the payment with stripe, which will either succeed or fail. If
 *    if succeeds, the user is redirected to the success page. If it fails, we
 *    request the backend to release the lock on the transaction.
 *
 * Held locks are released after a certain amount of time, dictated by the backend,
 * so an interruption in the flow will not permanently lock the order.
 */

import { LoaderFunction, redirect, useLoaderData } from "react-router-dom";

import { CURRENT_ORDER_QUERY_KEY } from "@/modules/cart/useCartDetails";
import { useManageCartAppointments } from "@/modules/cart/useManageCartAppointments";
import { CheckoutContainer } from "@/modules/checkout";
import { changeDayjsLocale } from "@/utils/dayjsUtils";
import { get, post } from "api";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async (request: Request) => {
  const platform = queryClient.fetchQuery(["platform"], () => get("/platform"));
  const user = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });
  const cartDetails = await queryClient.fetchQuery({
    queryKey: CURRENT_ORDER_QUERY_KEY,
    queryFn: () => get("/orders/current/details").catch(() => null),
  });

  /* When displaying the page in a mobile webview, we let the app handle displaying its own navbar */
  const showNavbar =
    new URL(request.url).searchParams.get("isMobileApp") !== "true";

  const data = await waitObj({ platform, user, cartDetails, showNavbar });

  changeDayjsLocale(data.platform.stripeRegion);

  let transactionDetails;
  if (data.cartDetails) {
    transactionDetails = await post(
      "/orders/{shopDisplayOrderId}/transactions",
      { shopDisplayOrderId: data.cartDetails.shopDisplayOrderId }
    );
  } else {
    transactionDetails = null;
  }

  return { ...data, transactionDetails };
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const checkoutElementLoader: LoaderFunction = async ({ request }) => {
  const data = await load(request);
  if (!data.cartDetails || !data.transactionDetails) {
    return redirect("/cart");
  }

  if (
    data.transactionDetails.freePurchase &&
    !data.transactionDetails.setupIntentId
  ) {
    return redirect(
      `/cart/checkout/success?shopDisplayOrderId=${data.cartDetails.shopDisplayOrderId}`
    );
  } else if (
    data.transactionDetails.transactionStatus === "Paid" ||
    data.transactionDetails.transactionStatus === "Processing"
  ) {
    return redirect(
      `/cart/checkout/success?shopDisplayOrderId=${data.cartDetails.shopDisplayOrderId}&orderTransactionId=${data.transactionDetails.orderTransactionId}`
    );
  }

  return data;
};

const CheckoutElementPage = () => {
  const { transactionDetails, platform, cartDetails, user } =
    useLoaderData() as LoaderData;

  // Manage cart appointments - automatically removes expired appointments
  // and redirects to cart if items are removed
  useManageCartAppointments({
    cartDetails,
    partnerEmrBookingEnabled:
      platform.platformSettings?.partnerEmrBookingEnabled,
  });

  return (
    <div className="CheckoutElementPage">
      <CheckoutContainer
        cartDetails={cartDetails}
        transactionDetails={transactionDetails}
        platform={platform}
        user={user}
        showNavbar={true}
      />
    </div>
  );
};

export default CheckoutElementPage;
